import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import ContactModal from '../components/ContactModal';
import TestimonialSlider from '../components/TestimonialSlider';
import MobileSlider from '../components/MobileSlider';
import WhyYouNeedSlider from '../components/WhyYouNeedSlider';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { Shield, CheckCircle, Lock, DollarSign, AlertCircle, TrendingUp } from 'lucide-react';

const TrademarkRegistration = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const whyYouNeedReasons = [
    {
      icon: Shield,
      title: 'Legal Protection',
      description: 'Secure exclusive nationwide rights to your brand name, logo, and intellectual property.',
    },
    {
      icon: Lock,
      title: 'Prevent Infringement',
      description: 'Stop competitors from using similar marks that could confuse customers.',
    },
    {
      icon: DollarSign,
      title: 'Increase Business Value',
      description: 'A registered trademark significantly increases your company\'s worth.',
    },
    {
      icon: AlertCircle,
      title: 'Avoid Legal Issues',
      description: 'Protect yourself from costly lawsuits over brand ownership.',
    },
  ];

  const steps = [
    {
      title: 'Information Collection',
      description: 'We gather comprehensive details about your trademark, including the mark itself, goods/services classification, and your business information to ensure accurate filing with the USPTO.',
    },
    {
      title: 'Research and Search Report',
      description: 'Our trademark attorneys conduct extensive searches through USPTO databases and common law sources to identify potential conflicts and assess your trademark\'s registrability.',
    },
    {
      title: 'Application and Submission',
      description: 'We prepare a professionally crafted application with all necessary documentation and submit it to the USPTO, ensuring compliance with all federal requirements.',
    },
  ];

  const packages = [
    {
      name: 'BASIC',
      price: '$45',
      priceNote: '*(Govt. filing fee not included)',
      features: [
        'Case Review',
        'Case Filing',
        'Basic Search',
        'State E-Filing',
        'Trademark Secured',
      ],
    },
    {
      name: 'DELUXE',
      price: '$145',
      priceNote: '*(Govt. filing fee not included)',
      features: [
        'Case Review',
        'Case Filing',
        'Basic Search',
        'State E-Filing',
        'Trademark Secured',
        'Direct Hit Search',
        'Dedicated Case Manager',
      ],
      popular: true,
    },
    {
      name: 'PREMIUM',
      price: '$299',
      priceNote: '*(Govt. filing fee not included)',
      features: [
        'Case Review (by a TM Expert)',
        'Basic Free Trademark Search',
        'Trademark Monitoring Services (3 Months)',
        'Trademark Secured',
        'Refusal Risk Meter',
        'Case filing service with complete paperwork',
      ],
    },
    {
      name: 'ELITE',
      price: '$599',
      priceNote: '*(Govt. filing fee not included)',
      features: [
        'Case Review & Filing (U.S Registered IP Attorney)',
        'Assigned Case Filing Officer',
        'Private Registration',
        'Trademark Secured',
        'Federal Search & Clearance Report (All States)',
        'Federal E-Filing (USPTO)',
      ],
    },
  ];

  const usps = [
    {
      title: 'Expert Legal Team',
      description: 'Our team consists of experienced trademark attorneys with deep knowledge of USPTO procedures and intellectual property law. We have successfully registered thousands of trademarks across all industries, ensuring your application is handled by professionals who understand the nuances of trademark law.',
    },
    {
      title: 'High Success Rate',
      description: 'With a 98% approval rate, we have one of the highest success rates in the industry. Our meticulous approach to trademark searches and application preparation minimizes the risk of rejection and ensures smooth processing.',
    },
    {
      title: 'Affordable Pricing',
      description: 'We believe trademark protection should be accessible to all businesses. Our competitive pricing, including the special $45 offer, makes professional trademark registration affordable without compromising quality or service.',
    },
    {
      title: 'Comprehensive Support',
      description: 'From initial consultation to final registration, we provide complete support throughout the trademark process. Our team is available to answer questions, address concerns, and guide you through every step of protecting your brand.',
    },
  ];

  const testimonials = [
    {
      name: 'Sarah Mitchell',
      company: 'TechFlow Solutions',
      text: 'Fine Trademark made the entire trademark registration process seamless. Their team was professional, responsive, and helped us secure our mark in record time.',
      rating: 5,
      image: 'https://images.unsplash.com/photo-1689600944138-da3b150d9cb8?w=200&h=200&fit=crop',
    },
    {
      name: 'James Anderson',
      company: 'Green Valley Organics',
      text: 'Excellent service from start to finish. The trademark search was thorough, and they handled all USPTO communications expertly.',
      rating: 5,
      image: 'https://images.pexels.com/photos/2182970/pexels-photo-2182970.jpeg?w=200&h=200&fit=crop',
    },
    {
      name: 'Maria Rodriguez',
      company: 'Urban Style Boutique',
      text: 'I was worried about the complexity of trademark registration, but Fine Trademark made it incredibly easy. Highly recommend their services!',
      rating: 5,
      image: 'https://images.unsplash.com/photo-1652471949169-9c587e8898cd?w=200&h=200&fit=crop',
    },
    {
      name: 'David Chen',
      company: 'Innovation Labs',
      text: 'Professional, efficient, and affordable. They successfully registered our trademark despite some initial concerns about potential conflicts.',
      rating: 5,
      image: 'https://images.unsplash.com/photo-1652471943570-f3590a4e52ed?w=200&h=200&fit=crop',
    },
    {
      name: 'Emily Thompson',
      company: 'Wellness First',
      text: 'The $45 offer is unbeatable! Great value for professional trademark registration services. My brand is now fully protected.',
      rating: 5,
      image: 'https://images.unsplash.com/photo-1655249481446-25d575f1c054?w=200&h=200&fit=crop',
    },
  ];

  const faqs = [
    {
      question: 'What is a trademark and why do I need one?',
      answer: 'A trademark is a distinctive sign, symbol, word, or phrase that identifies and distinguishes your goods or services from others. Registering a trademark provides legal protection, prevents others from using similar marks, establishes nationwide rights, and adds value to your business.',
    },
    {
      question: 'How long does the trademark registration process take?',
      answer: 'The complete trademark registration process typically takes 8-12 months from filing to final registration. However, you receive a filing date immediately upon submission, which establishes your priority. We file applications within 24-48 hours of receiving all necessary information.',
    },
    {
      question: 'What happens if my trademark application is rejected?',
      answer: 'If the USPTO issues an office action or rejection, our attorneys will analyze the reasons and develop a strategic response. Depending on your package, we handle office action responses to address examiner concerns and work towards approval.',
    },
    {
      question: 'Can I trademark a name that is similar to an existing trademark?',
      answer: 'It depends on several factors including the similarity of the marks, the relatedness of the goods/services, and the likelihood of consumer confusion. Our comprehensive trademark search helps identify potential conflicts before filing.',
    },
    {
      question: 'What\'s included in the $45 trademark registration offer?',
      answer: 'Our $45 special offer includes case review, case filing, basic search, state e-filing, and trademark secured status. This is our most affordable package designed to make trademark protection accessible to all businesses. Government filing fees are additional.',
    },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#1E1E1E] via-[#2a2a2a] to-[#1E1E1E] text-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-4xl mx-auto">
            <Badge className="bg-[#E5B80B] text-[#1E1E1E] hover:bg-[#E5B80B] text-sm px-4 py-1 mb-6">
              Cost-Effective Solutions
            </Badge>
            <h1 className="page-title text-white mb-6 animate-in fade-in slide-in-from-top duration-700">
              Trademark Registration Services
            </h1>
            <p className="body-large text-gray-300 mb-8 animate-in fade-in slide-in-from-bottom duration-700">
              Protect your brand identity with professional trademark registration. Secure exclusive rights to your brand name, logo, and slogan across the United States.
            </p>
            <Button 
              size="lg" 
              className="bg-[#E5B80B] hover:bg-[#c99f09] text-[#1E1E1E] font-semibold px-12 text-lg h-14 transition-all duration-300 hover:scale-105"
              onClick={() => setIsModalOpen(true)}
            >
              Register Your Trademark
            </Button>
          </div>
        </div>
      </section>

      {/* Why You Need Trademark Registration */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-5xl font-bold text-[#1E1E1E] mb-4">Why You Need Trademark Registration</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Essential reasons to protect your brand with trademark registration
            </p>
          </div>

          {/* Mobile/Tablet Slider */}
          <div className="lg:hidden">
            <WhyYouNeedSlider reasons={whyYouNeedReasons} />
          </div>

          {/* Desktop Grid */}
          <div className="hidden lg:grid lg:grid-cols-4 gap-8">
            {whyYouNeedReasons.map((reason, index) => (
              <div key={index} className="text-center">
                <div className="flex justify-center mb-6">
                  <div className="p-6 bg-[#E5B80B]/10 rounded-full">
                    <reason.icon className="h-12 w-12 text-[#E5B80B]" />
                  </div>
                </div>
                <h3 className="text-xl font-bold text-[#1E1E1E] mb-4">{reason.title}</h3>
                <p className="text-gray-600 leading-relaxed">{reason.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Service */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          {/* Mobile/Tablet - Image First, Then Content */}
          <div className="lg:hidden space-y-8">
            <div className="relative h-64 sm:h-80 rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=800"
                alt="Trademark Registration"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="space-y-4">
              <h2 className="text-2xl sm:text-3xl font-bold text-[#1E1E1E]">About Trademark Registration</h2>
              <p className="text-base text-gray-600 leading-relaxed">
                Trademark registration is a critical step in protecting your brand's identity and establishing your legal rights to use your mark in commerce. A federally registered trademark provides nationwide protection, legal presumption of ownership, and the exclusive right to use your mark on or in connection with your goods or services.
              </p>
              <p className="text-base text-gray-600 leading-relaxed">
                Our trademark registration service handles every aspect of the process, from conducting comprehensive searches to filing your application with the United States Patent and Trademark Office (USPTO). We ensure your application is properly classified, accurately described, and submitted with all required documentation to maximize your chances of approval.
              </p>
            </div>
          </div>

          {/* Desktop Layout */}
          <div className="hidden lg:grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <h2 className="section-heading">About Trademark Registration</h2>
              <p className="body-regular">
                Trademark registration is a critical step in protecting your brand's identity and establishing your legal rights to use your mark in commerce. A federally registered trademark provides nationwide protection, legal presumption of ownership, and the exclusive right to use your mark on or in connection with your goods or services.
              </p>
              <p className="body-regular">
                Our trademark registration service handles every aspect of the process, from conducting comprehensive searches to filing your application with the United States Patent and Trademark Office (USPTO). We ensure your application is properly classified, accurately described, and submitted with all required documentation to maximize your chances of approval.
              </p>
            </div>
            <div className="relative h-96 rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=800"
                alt="Trademark Registration"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* How We Work - Simple 3-Step Process */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-5xl font-bold text-[#1E1E1E] mb-4 lg:mb-6">Our Registration Process</h2>
            <p className="text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              We follow a proven three-step process to ensure your trademark is registered efficiently and correctly.
            </p>
          </div>

          {/* Mobile/Tablet Slider */}
          <div className="lg:hidden pb-12">
            <MobileSlider items={steps} type="steps" />
          </div>

          {/* Desktop Grid */}
          <div className="hidden lg:grid lg:grid-cols-3 gap-8">
            {steps.map((step, index) => (
              <div key={index} className="relative">
                <div className="flex flex-col items-center text-center">
                  <div className="w-20 h-20 rounded-full bg-[#E5B80B] text-[#1E1E1E] flex items-center justify-center text-3xl font-bold mb-6 shadow-lg">
                    {index + 1}
                  </div>
                  <h3 className="text-2xl font-bold text-[#1E1E1E] mb-4">{step.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{step.description}</p>
                </div>
                {index < steps.length - 1 && (
                  <div className="hidden lg:block absolute top-10 left-1/2 w-full h-0.5 bg-[#E5B80B]/30"></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="section-heading mb-6">Cost-Effective Trademark Solutions for Every Business</h2>
            <p className="body-large max-w-3xl mx-auto">
              At Fine Trademark, we provide affordable trademark registration services designed to suit businesses of all sizes. Our pricing consistently beats the market average, ensuring you receive top-notch protection without exceeding your budget.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {packages.map((pkg, index) => (
              <Card
                key={index}
                className={`relative ${pkg.popular ? 'border-4 border-[#E5B80B] shadow-2xl scale-105' : 'border-2'}`}
              >
                {pkg.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <Badge className="bg-[#E5B80B] text-[#1E1E1E] text-sm px-4 py-1">Most Popular</Badge>
                  </div>
                )}
                <CardContent className="pricing-card p-6">
                  <div className="pricing-header">
                    <h3 className="card-title text-white mb-2">{pkg.name}</h3>
                    <div className="border-t border-white/30 w-12 mx-auto my-2"></div>
                    <span className="text-4xl font-bold">{pkg.price}</span>
                  </div>
                  <div className="pricing-features mt-6">
                    <ul className="space-y-3">
                      {pkg.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start">
                          <CheckCircle className="h-5 w-5 text-[#E5B80B] mr-2 mt-0.5 flex-shrink-0" />
                          <span className="body-small text-gray-700">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <p className="text-xs text-gray-500 mb-4 text-center mt-4">{pkg.priceNote}</p>
                  <Button
                    className={`w-full h-12 font-semibold transition-all duration-300 mt-auto ${
                      pkg.popular
                        ? 'bg-[#E5B80B] hover:bg-[#c99f09] text-[#1E1E1E]'
                        : 'bg-[#1E1E1E] hover:bg-[#2a2a2a] text-white'
                    }`}
                    onClick={() => setIsModalOpen(true)}
                  >
                    Get Started
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="section-heading mb-6">Why Choose Our Trademark Services</h2>
            <div className="max-w-3xl mx-auto space-y-6">
              <p className="body-regular">
                With over 15 years of experience in trademark law and intellectual property protection, we have established ourselves as a trusted partner for businesses seeking comprehensive trademark registration services. Our team's expertise spans all industries, from technology startups to established enterprises.
              </p>
              <p className="body-regular">
                We understand that every trademark is unique, which is why we provide personalized attention to each application. Our attorneys take the time to understand your business, assess your trademark's strength, and develop strategies to maximize protection while minimizing potential obstacles.
              </p>
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {usps.map((usp, index) => (
              <Card key={index} className="hover:shadow-xl transition-shadow duration-300">
                <CardContent className="p-8">
                  <h3 className="sub-heading mb-4">{usp.title}</h3>
                  <p className="body-regular">{usp.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-[#E5B80B] to-[#c99f09]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="section-heading text-[#1E1E1E] mb-6">Register Your Trademark Today - Starting at $45</h2>
          <p className="body-large text-[#1E1E1E]/90 mb-8">
            Take advantage of our affordable pricing and secure your brand's legal protection. Don't risk losing your brand identity to competitors.
          </p>
          <Button 
            size="lg" 
            className="bg-[#1E1E1E] hover:bg-[#2a2a2a] text-white font-semibold px-12 text-lg h-14 transition-all duration-300 hover:scale-105"
            onClick={() => setIsModalOpen(true)}
          >
            Start Your Registration
          </Button>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-20">
            <h2 className="section-heading mb-4">Client Success Stories</h2>
          </div>
          <TestimonialSlider testimonials={testimonials} />
        </div>
      </section>

      {/* FAQs */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="section-heading mb-4">Frequently Asked Questions</h2>
          </div>
          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem key={index} value={`item-${index}`} className="bg-white border border-gray-200 rounded-lg px-6">
                <AccordionTrigger className="text-left card-title hover:text-[#E5B80B]">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="body-regular">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>
      
      <ContactModal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)}
        serviceName="Trademark Registration"
      />
    </div>
  );
};

export default TrademarkRegistration;
